import React, { createContext, useEffect, useState } from "react"
import { Switch, Route, useLocation } from "react-router-dom"

import {
  loadVersion,
  loadCountries,
  loadReasonCodes,
  loadSeasons,
  loadServiceTypes,
  logonUser,
  logoutUser
} from "../containers/applicationSlice"

import NavBar from "../components/generic/navbar"
import {
  getVersion,
  getCountries,
  getReasonCodes,
  getSeasons,
  getServiceTypes,
  getUserInfo
} from "../api/BackendCalls"

import Notfound from "../pages/Notfound"
import { routes, RouteType } from "./routeConfig"
import Notifications from "./Notifications"
import { LoadingPanel } from "../components/generic"
import { FlightReports } from "../api/FlightReport"
import PrivateRoute from "./PrivateRoute"
import { handleRemoveNotifications } from "../pages/Support"
import LoginRoute from "./LoginRoute"
import { getSelectionSliceStorage } from "../components/LocalStorageMiddleware"
import { updateSelectionSlice } from "../containers/selectionSlice"
import { useAppDispatch, useAppSelector } from "./hooks"

export interface ILoadingInfo {
  isLoading: boolean
  loadingTxt?: string
  fromFailure?: boolean
  loadingSubTxt?: string
}

export interface IAppContext {
  isLoading: boolean
  handleSetIsLoading: (loadingInfo: ILoadingInfo) => void
  flightReports: FlightReports
  handleChangeFlightReports: (reports: FlightReports) => void
  isDownloading: boolean
  handleSetIsDownloading: (downloading: boolean) => void
  userChanged: boolean
  handleChangeUserChanged: (userChanged: boolean) => void
  pressedGoBack: boolean
  handleChangePressedGoBack: (pressedGoBack: boolean) => void
}

export const AppContext = createContext<IAppContext>({
  isLoading: false,
  handleSetIsLoading: () => {},
  flightReports: [],
  handleChangeFlightReports: () => {},
  isDownloading: false,
  handleSetIsDownloading: () => {},
  userChanged: false,
  handleChangeUserChanged: () => {},
  pressedGoBack: false,
  handleChangePressedGoBack: () => {}
})

const App: React.FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [userChanged, setUserChanged] = useState(false)
  const [loadingTxt, setLoadingTxt] = useState("")
  const [loadingSubTxt, setLoadingSubTxt] = useState("")
  const [pressedGoBack, setPressedGoBack] = useState(false)
  const [disableLoadingTimeOut, setDisableLoadingTimeOut] = useState(false)
  const [flightReports, setFlightReports] = useState<FlightReports>([])
  const { userInfo, versionInfo } = useAppSelector(
    (state) => state["appReducer"]
  )
  const [userName, setUserName] = useState(userInfo.loginName)

  const pageId: string = "APPROOT"

  const dispatch = useAppDispatch()

  const location = useLocation()

  async function doInitialLoad() {
    getVersion(pageId, (version) => {
      dispatch(loadVersion(version))
      // wait for version response to get the new session cookie
      doLoadUserInfo()
      doLoadSeasons()
      doLoadCountries()
      doLoadServiceTypes()
      doLoadReasonCodes()
    })
  }

  async function doLoadSeasons() {
    getSeasons(pageId, (seasons) => {
      dispatch(loadSeasons(seasons))
    })
  }

  async function doLoadCountries() {
    getCountries(pageId, (countries) => {
      dispatch(loadCountries(countries))
    })
  }

  async function doLoadServiceTypes() {
    getServiceTypes(pageId, (stList) => dispatch(loadServiceTypes(stList)))
  }

  async function doLoadReasonCodes() {
    getReasonCodes(pageId, (rcList) => {
      dispatch(loadReasonCodes(rcList))
    })
  }

  async function doLoadUserInfo() {
    getUserInfo(
      pageId,
      (userName) => {
        dispatch(logonUser({ loginName: userName, authenticated: true }))
      },
      () => dispatch(logoutUser({}))
    )
  }

  useEffect(() => {
    document.title = versionInfo.siteName
  }, [versionInfo.siteName])

  useEffect(() => {
    doInitialLoad()

    // eslint-disable-next-line
  }, []) // [] implies only on mount

  useEffect(() => {
    handleRemoveNotifications()
  }, [location])

  useEffect(() => {
    if (userInfo?.loginName !== undefined) {
      const newSelectionReducer = getSelectionSliceStorage(
        userInfo.authenticated ? userInfo.loginName : "DEFAULT"
      )
      dispatch(updateSelectionSlice(newSelectionReducer))
      setUserName(userInfo.loginName)
    }
    if (userName !== userInfo.loginName) {
      setUserChanged(true)
    }
    // eslint-disable-next-line
  }, [userInfo])

  const appContextValue: IAppContext = {
    isLoading: isLoading,
    handleSetIsLoading: (loadingInfo) => {
      setIsLoading(loadingInfo.isLoading)
      if (loadingInfo.isLoading && loadingInfo.loadingTxt) {
        setLoadingTxt(loadingInfo.loadingTxt)
      }
      if (loadingInfo.isLoading && loadingInfo.loadingSubTxt) {
        setLoadingSubTxt(loadingInfo.loadingSubTxt)
      }
      setDisableLoadingTimeOut(
        loadingInfo.isLoading ? false : loadingInfo.fromFailure ? true : false
      )
    },
    flightReports: flightReports,
    handleChangeFlightReports: (reports) => {
      setFlightReports(reports)
    },
    isDownloading: isDownloading,
    handleSetIsDownloading: (downloading) => {
      setIsDownloading(downloading)
    },
    userChanged: userChanged,
    handleChangeUserChanged: setUserChanged,
    pressedGoBack: pressedGoBack,
    handleChangePressedGoBack: setPressedGoBack
  }

  const isLogin = location.pathname === "/login"

  return (
    <AppContext.Provider value={appContextValue}>
      <div className="ocs-app">
        {!isLogin && <Notifications />}
        <NavBar />
        <div className="ocs-container">
          <Switch>
            {routes.map((route, key) => {
              if (route.routeType && route.routeType === RouteType.Private) {
                return (
                  <PrivateRoute
                    path={route.path}
                    exact={route.exact === false ? false : true}
                    key={`route-${key}`}
                  >
                    {route.render}
                  </PrivateRoute>
                )
              } else if (
                route.routeType &&
                route.routeType === RouteType.Login
              ) {
                return (
                  <LoginRoute
                    path={route.path}
                    exact={route.exact === false ? false : true}
                    key={`route-${key}`}
                  >
                    {route.render}
                  </LoginRoute>
                )
              } else {
                return (
                  <Route
                    path={route.path}
                    render={() => route.render}
                    exact={route.exact === false ? false : true}
                    key={`route-${key}`}
                  />
                )
              }
            })}

            <Route component={Notfound} />
          </Switch>
        </div>
        <LoadingPanel
          visible={isLoading}
          loadingTxt={loadingTxt}
          loadingSubTxt={loadingSubTxt}
          disableTimeout={disableLoadingTimeOut}
          callback={() => setDisableLoadingTimeOut(false)}
        />
      </div>
    </AppContext.Provider>
  )
}

export default App
